import classNames from "classnames";
import type { ImgHTMLAttributes } from "react";
import React, { useEffect, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";

import { rgbaToThumbHash, thumbHashToDataURL } from "@utils/thumbhash";

interface IProps extends ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
  alt?: string;
  srcError?: string;
  isLazy?: boolean;
  width?: number;
  height?: number;
  className?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  scrollPosition?: { x: number; y: number };
}

function Image({ src = "", alt = "", isLazy = true, width = 1080, height = 1350, className, ...props }: IProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [thumbnail, setThumbnail] = useState("");

  const generateThumbHash = async () => {
    // 1. 원본 이미지에서 필요한 데이터를 꺼냅니다.
    const image = new window.Image();
    image.src = `${src}?width=20&height=20`;
    image.crossOrigin = "Anonymous";

    await new Promise((resolve) => (image.onload = resolve));
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (context === null) return;

    const scale = 100 / Math.max(image.width, image.height);
    canvas.width = Math.round(image.width * scale);
    canvas.height = Math.round(image.height * scale);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);

    // 2. ThumbHash 알고리즘을 적용시킵니다.
    const binaryThumbHash = rgbaToThumbHash(pixels.width, pixels.height, pixels.data);

    // 3. dataURL 형태로 변환합니다. 이걸 렌더링하면 됩니다.
    const placeholderURL = thumbHashToDataURL(binaryThumbHash);
    setThumbnail(placeholderURL);
  };

  useEffect(() => {
    generateThumbHash();
  }, []);

  return (
    <picture
      className={classNames("bg-tuneGrey-100 relative inline-block h-fit overflow-hidden rounded-sm", className)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {isLazy && thumbnail ? (
        <img
          alt={alt}
          className={classNames(
            "center pointer-events-none absolute size-full scale-300 cursor-none",
            isLoaded ? "opacity-0" : "opacity-100",
          )}
          src={thumbnail}
          {...props}
        />
      ) : null}
      <LazyLoadImage
        {...props}
        className={classNames("size-full object-contain", isLoaded ? "opacity-100" : "opacity-0")}
        height={height}
        onLoad={() => {
          setTimeout(() => {
            setIsLoaded(true);
          }, 500);
        }}
        src={src}
        visibleByDefault={isLazy}
        width={width}
      />
    </picture>
  );
}

export default Image;
